import type { Car } from '../types/car.js';
import type { Driver } from '../types/driver.js';
import { getNationalityEntry, normalizeCountryCode } from '../types/nationalities.js';

export const CAR_BACKUP_FORMAT = 'multirx-cars' as const;
export const DRIVER_BACKUP_FORMAT = 'multirx-drivers' as const;
export const GARAGE_BACKUP_VERSION = 1;

export type CarBackupEntry = Omit<Car, 'id'> & { sourceId?: number };
export type DriverBackupEntry = Omit<Driver, 'id'> & { sourceId?: number };

export interface CarBackupFile {
  format: typeof CAR_BACKUP_FORMAT;
  version: typeof GARAGE_BACKUP_VERSION;
  exportedAt: string;
  cars: CarBackupEntry[];
}

export interface DriverBackupFile {
  format: typeof DRIVER_BACKUP_FORMAT;
  version: typeof GARAGE_BACKUP_VERSION;
  exportedAt: string;
  drivers: DriverBackupEntry[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function clampStarStat(value: unknown, fallback = 2): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.min(5, Math.round(n)));
}

function clampDivision(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(4, Math.round(n)));
}

function parseImagePath(value: unknown): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function carBackupEntryFromSaved(car: Car): CarBackupEntry {
  const { id, ...rest } = car;
  return { ...rest, ...(id != null ? { sourceId: id } : {}) };
}

export function driverBackupEntryFromSaved(driver: Driver): DriverBackupEntry {
  const { id, ...rest } = driver;
  return { ...rest, ...(id != null ? { sourceId: id } : {}) };
}

export function buildCarBackupFile(
  entries: CarBackupEntry[],
  exportedAt: string = new Date().toISOString()
): CarBackupFile {
  return { format: CAR_BACKUP_FORMAT, version: GARAGE_BACKUP_VERSION, exportedAt, cars: entries };
}

export function buildDriverBackupFile(
  entries: DriverBackupEntry[],
  exportedAt: string = new Date().toISOString()
): DriverBackupFile {
  return {
    format: DRIVER_BACKUP_FORMAT,
    version: GARAGE_BACKUP_VERSION,
    exportedAt,
    drivers: entries,
  };
}

export function carBackupFilename(exportedAt: Date = new Date()): string {
  const stamp = exportedAt.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `multirx-voitures-${stamp}.json`;
}

export function driverBackupFilename(exportedAt: Date = new Date()): string {
  const stamp = exportedAt.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `multirx-pilotes-${stamp}.json`;
}

function parseCarBackupEntry(raw: unknown, index: number): CarBackupEntry {
  if (!isRecord(raw)) {
    throw new Error(`Voiture ${index + 1} : format invalide`);
  }
  if (typeof raw.name !== 'string' || !raw.name.trim()) {
    throw new Error(`Voiture ${index + 1} : nom requis`);
  }
  const entry: CarBackupEntry = {
    name: raw.name.trim(),
    engine: clampStarStat(raw.engine),
    tire: clampStarStat(raw.tire),
    division: clampDivision(raw.division),
    imagePath: parseImagePath(raw.imagePath),
  };
  if (typeof raw.sourceId === 'number') entry.sourceId = raw.sourceId;
  else if (typeof raw.id === 'number') entry.sourceId = raw.id;
  return entry;
}

function parseDriverBackupEntry(raw: unknown, index: number): DriverBackupEntry {
  if (!isRecord(raw)) {
    throw new Error(`Pilote ${index + 1} : format invalide`);
  }
  if (typeof raw.firstName !== 'string' || !raw.firstName.trim()) {
    throw new Error(`Pilote ${index + 1} : prénom requis`);
  }
  if (typeof raw.lastName !== 'string' || !raw.lastName.trim()) {
    throw new Error(`Pilote ${index + 1} : nom requis`);
  }
  const nationality = normalizeCountryCode(
    typeof raw.nationality === 'string' ? raw.nationality : ''
  );
  if (!nationality || !getNationalityEntry(nationality)) {
    throw new Error(`Pilote ${index + 1} : nationalité invalide`);
  }
  const entry: DriverBackupEntry = {
    firstName: raw.firstName.trim(),
    lastName: raw.lastName.trim(),
    nationality,
    pilotage: clampStarStat(raw.pilotage),
    defense: clampStarStat(raw.defense),
    division: clampDivision(raw.division),
    imagePath: parseImagePath(raw.imagePath) ?? null,
  };
  if (typeof raw.sourceId === 'number') entry.sourceId = raw.sourceId;
  else if (typeof raw.id === 'number') entry.sourceId = raw.id;
  return entry;
}

export function parseCarBackupPayload(raw: unknown): CarBackupEntry[] {
  if (isRecord(raw) && raw.name && !raw.cars) {
    return [parseCarBackupEntry(raw, 0)];
  }
  if (!isRecord(raw) || raw.format !== CAR_BACKUP_FORMAT) {
    throw new Error('Fichier non reconnu (format multirx-cars attendu)');
  }
  if (raw.version !== GARAGE_BACKUP_VERSION) {
    throw new Error(`Version de sauvegarde non supportée : ${String(raw.version)}`);
  }
  if (!Array.isArray(raw.cars) || raw.cars.length === 0) {
    throw new Error('Aucune voiture dans le fichier');
  }
  return raw.cars.map((entry, index) => parseCarBackupEntry(entry, index));
}

export function parseDriverBackupPayload(raw: unknown): DriverBackupEntry[] {
  if (isRecord(raw) && raw.firstName && raw.lastName && !raw.drivers) {
    return [parseDriverBackupEntry(raw, 0)];
  }
  if (!isRecord(raw) || raw.format !== DRIVER_BACKUP_FORMAT) {
    throw new Error('Fichier non reconnu (format multirx-drivers attendu)');
  }
  if (raw.version !== GARAGE_BACKUP_VERSION) {
    throw new Error(`Version de sauvegarde non supportée : ${String(raw.version)}`);
  }
  if (!Array.isArray(raw.drivers) || raw.drivers.length === 0) {
    throw new Error('Aucun pilote dans le fichier');
  }
  return raw.drivers.map((entry, index) => parseDriverBackupEntry(entry, index));
}
